import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    Download,
    Share2,
    Star,
    ZoomIn,
    ZoomOut,
    RotateCw,
    Maximize,
    ChevronLeft,
    ChevronRight,
    Play,
    Pause,
    Volume2,
    VolumeX,
    FileText,
    Eye,
    AlertTriangle,
    Loader,
    RotateCcw,
    Maximize2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TextCodeViewer from './TextCodeViewer';
import DocumentViewer from './DocViewer';

const FileViewer = ({
    file,
    isOpen,
    onClose,
    onDownload,
    onShare,
    onStar,
    onNext,
    onPrevious,
    hasNext = false,
    hasPrevious = false
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState(100);
    const [rotation, setRotation] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fileUrl, setFileUrl] = useState(null);

    // Image pan/drag state
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });

    // Video/Audio controls
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);

    const mediaRef = useRef(null);
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const imageContainerRef = useRef(null);
    const imageRef = useRef(null);
    const { FILE_APP_URL, user } = useAuth();


    // Reset state when file changes
    useEffect(() => {
        if (file) {
            setLoading(true);
            setError(null);
            setZoom(100);
            setRotation(0);
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
            setImagePosition({ x: 0, y: 0 });
            loadFileUrl();
        }
    }, [file]);

    const loadFileUrl = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${FILE_APP_URL}/files/${file.id}/direct-download`, {
                credentials: 'include'
            });

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                let errorMessage = `Failed to load file (${response.status})`;

                if (contentType && contentType.includes('application/json')) {
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.error || errorData.message || errorMessage;
                    } catch (jsonError) {
                        console.error('Failed to parse error response as JSON:', jsonError);
                    }
                } else {
                    try {
                        const textResponse = await response.text();
                        if (textResponse) {
                            errorMessage = textResponse.substring(0, 200);
                        }
                    } catch (textError) {
                        console.error('Failed to parse error response as text:', textError);
                    }
                }

                throw new Error(errorMessage);
            }

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                setFileUrl(data.view_url || data.download_url);
            } else {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setFileUrl(blobUrl);
            }

            setLoading(false);
        } catch (err) {
            console.error('Error loading file:', err);
            setError(err.message || 'Failed to load file');
            setLoading(false);
        }
    };

    const getFileType = () => {
        if (!file) return 'unknown';

        const extension = file.original_name?.split('.').pop()?.toLowerCase() || '';
        const mimeType = file.mime_type?.toLowerCase() || '';

        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension) ||
            mimeType.startsWith('image/')) {
            return 'image';
        }

        if (['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(extension) ||
            mimeType.startsWith('video/')) {
            return 'video';
        }

        if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac', 'wma'].includes(extension) ||
            mimeType.startsWith('audio/')) {
            return 'audio';
        }

        if (extension === 'pdf' || mimeType === 'application/pdf') {
            return 'pdf';
        }

        if (['txt', 'md', 'json', 'xml', 'csv', 'log'].includes(extension) ||
            mimeType.startsWith('text/')) {
            return 'text';
        }

        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
            return 'office';
        }

        if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'css', 'html', 'php', 'rb', 'go', 'rs', 'lua'].includes(extension)) {
            return 'code';
        }

        return 'unknown';
    };

    // Image zoom and pan functions
    const handleZoomIn = () => {
        setZoom(prev => {
            const newZoom = Math.min(prev + 25, 500);
            if (newZoom > 100) {
                // Reset position when zooming in beyond 100%
                centerImage();
            }
            return newZoom;
        });
    };

    const handleZoomOut = () => {
        setZoom(prev => {
            const newZoom = Math.max(prev - 25, 25);
            if (newZoom <= 100) {
                // Reset position when zooming out to 100% or less
                setImagePosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    const handleRotate = () => setRotation(prev => (prev + 90) % 360);

    const centerImage = () => {
        setImagePosition({ x: 0, y: 0 });
        if (imageContainerRef.current) {
            imageContainerRef.current.scrollTo({
                left: imageContainerRef.current.scrollWidth / 2 - imageContainerRef.current.clientWidth / 2,
                top: imageContainerRef.current.scrollHeight / 2 - imageContainerRef.current.clientHeight / 2,
                behavior: 'smooth'
            });
        }
    };

    const handleZoomToFit = () => {
        setZoom(100);
        setRotation(0);
        setImagePosition({ x: 0, y: 0 });
        if (imageContainerRef.current) {
            imageContainerRef.current.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        }
    };

    // Mouse drag handlers for image panning
    const handleMouseDown = (e) => {
        if (zoom <= 100) return; // Only allow dragging when zoomed in
        setIsDragging(true);
        setDragStart({
            x: e.clientX - imagePosition.x,
            y: e.clientY - imagePosition.y
        });
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoom <= 100) return;

        const newPosition = {
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        };

        setImagePosition(newPosition);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        if (zoom <= 100 || e.touches.length !== 1) return;
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
            x: touch.clientX - imagePosition.x,
            y: touch.clientY - imagePosition.y
        });
        e.preventDefault();
    };

    const handleTouchMove = (e) => {
        if (!isDragging || zoom <= 100 || e.touches.length !== 1) return;

        const touch = e.touches[0];
        const newPosition = {
            x: touch.clientX - dragStart.x,
            y: touch.clientY - dragStart.y
        };

        setImagePosition(newPosition);
        e.preventDefault();
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // Wheel zoom for images
    const handleWheel = (e) => {
        if (getFileType() !== 'image') return;

        e.preventDefault();
        const delta = e.deltaY;
        const zoomFactor = delta > 0 ? -25 : 25;

        setZoom(prev => {
            const newZoom = Math.max(25, Math.min(prev + zoomFactor, 500));
            if (newZoom <= 100) {
                setImagePosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    useEffect(() => {
        const container = imageContainerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
            return () => container.removeEventListener('wheel', handleWheel);
        }
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDragging, dragStart, imagePosition]);

    const handleFullscreen = () => {
        if (!isFullscreen && viewerRef.current?.requestFullscreen) {
            viewerRef.current.requestFullscreen();
            setIsFullscreen(true);
        } else if (isFullscreen && document.exitFullscreen) {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handlePlayPause = () => {
        if (mediaRef.current) {
            if (isPlaying) {
                mediaRef.current.pause();
            } else {
                mediaRef.current.play();
            }
        }
    };

    const handleTimeUpdate = () => {
        if (mediaRef.current) {
            setCurrentTime(mediaRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (mediaRef.current) {
            setDuration(mediaRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const newTime = percent * duration;
        if (mediaRef.current) {
            mediaRef.current.currentTime = newTime;
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'Escape':
                onClose();
                break;
            case 'ArrowLeft':
                if (hasPrevious) onPrevious();
                break;
            case 'ArrowRight':
                if (hasNext) onNext();
                break;
            case ' ':
                e.preventDefault();
                if (getFileType() === 'video' || getFileType() === 'audio') {
                    handlePlayPause();
                }
                break;
            case '+':
            case '=':
                if (getFileType() === 'image') handleZoomIn();
                break;
            case '-':
                if (getFileType() === 'image') handleZoomOut();
                break;
            case 'r':
                if (getFileType() === 'image') handleRotate();
                break;
            case '0':
                if (getFileType() === 'image') handleZoomToFit();
                break;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, hasNext, hasPrevious, isPlaying]);

    const renderFileContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Loader className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Loading file...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">Failed to load file</p>
                        <p className="text-sm text-gray-500">{error}</p>
                        <button
                            onClick={loadFileUrl}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        const fileType = getFileType();

        switch (fileType) {
            case 'image':
                return (
                    <div className="relative h-full w-full">
                        <div
                            ref={imageContainerRef}
                            className="image-viewer-container"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                overflow: 'auto',
                                cursor: zoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                            }}
                        >
                            <div
                                className="image-wrapper"
                                style={{
                                    minWidth: '100%',
                                    minHeight: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: zoom > 100 ? '50px' : '0'
                                }}
                            >
                                <img
                                    ref={imageRef}
                                    src={fileUrl}
                                    alt={file.original_name}
                                    className="zoomable-image"
                                    style={{
                                        transform: `scale(${zoom / 100}) rotate(${rotation}deg) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                                        transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        maxWidth: zoom <= 100 ? '100%' : 'none',
                                        maxHeight: zoom <= 100 ? '100%' : 'none',
                                        objectFit: 'contain',
                                        userSelect: 'none',
                                        WebkitUserDrag: 'none'
                                    }}
                                    onLoad={() => setLoading(false)}
                                    onError={() => setError('Failed to load image')}
                                    onMouseDown={handleMouseDown}
                                    onTouchStart={handleTouchStart}
                                />
                            </div>
                        </div>

                        {/* Zoom indicator */}
                        {zoom !== 100 && (
                            <div className="zoom-indicator">
                                {zoom}%
                            </div>
                        )}
                    </div>
                );

            case 'video':
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="relative w-full h-full max-w-4xl">
                            <video
                                ref={mediaRef}
                                src={fileUrl}
                                className="w-full h-full object-contain"
                                onLoadedMetadata={handleLoadedMetadata}
                                onTimeUpdate={handleTimeUpdate}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onEnded={() => setIsPlaying(false)}
                            />

                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={handlePlayPause}
                                        className="text-white hover:text-blue-400"
                                    >
                                        {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                                    </button>

                                    <div className="flex-1">
                                        <div
                                            className="h-2 bg-white/30 rounded-full cursor-pointer"
                                            onClick={handleSeek}
                                        >
                                            <div
                                                className="h-full bg-blue-500 rounded-full"
                                                style={{ width: `${(currentTime / duration) * 100}%` }}
                                            />
                                        </div>
                                    </div>

                                    <span className="text-white text-sm">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </span>

                                    <button
                                        onClick={() => {
                                            setIsMuted(!isMuted);
                                            if (mediaRef.current) {
                                                mediaRef.current.muted = !isMuted;
                                            }
                                        }}
                                        className="text-white hover:text-blue-400"
                                    >
                                        {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center max-w-md w-full">
                            <div className="mb-8">
                                <div className="h-32 w-32 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Volume2 className="h-16 w-16 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{file.original_name}</h3>
                                <p className="text-gray-600">Audio File</p>
                            </div>

                            <audio
                                ref={mediaRef}
                                src={fileUrl}
                                onLoadedMetadata={handleLoadedMetadata}
                                onTimeUpdate={handleTimeUpdate}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onEnded={() => setIsPlaying(false)}
                                className="hidden"
                            />

                            <div className="space-y-4">
                                <div className="flex items-center justify-center space-x-6">
                                    <button
                                        onClick={handlePlayPause}
                                        className="h-16 w-16 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white"
                                    >
                                        {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8 ml-1" />}
                                    </button>
                                </div>

                                <div>
                                    <div
                                        className="h-2 bg-gray-300 rounded-full cursor-pointer mb-2"
                                        onClick={handleSeek}
                                    >
                                        <div
                                            className="h-full bg-blue-500 rounded-full"
                                            style={{ width: `${(currentTime / duration) * 100}%` }}
                                        />
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>{formatTime(currentTime)}</span>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'pdf':
                return (
                    <div className="h-full w-full bg-gray-100">
                        <iframe
                            src={fileUrl}
                            className="w-full h-full border-0"
                            title={file.original_name}
                            type="application/pdf"
                        />
                    </div>
                );

            case 'text':
            case 'code':
                // CRITICAL FIX: Remove any wrapper divs that might constrain height
                return (
                    <TextCodeViewer
                        fileUrl={fileUrl}
                        fileName={file.original_name}
                        fileType={fileType}
                    />
                );

            case 'office':
                return (
                    <DocumentViewer
                        fileUrl={fileUrl}
                        fileName={file.original_name}
                        onDownload={onDownload}
                        file={file}
                    />
                );

            default:
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <Eye className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-white mb-2">Preview Not Available</h3>
                            <p className="text-gray-600 mb-6">{file.original_name}</p>
                            <div className="space-y-3">
                                <p className="text-sm text-gray-500">
                                    This file type cannot be previewed in the browser.
                                </p>
                                <button
                                    onClick={() => onDownload(file)}
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download File
                                </button>
                            </div>
                        </div>
                    </div>
                );
        }
    };

    if (!isOpen || !file) return null;

    const fileType = getFileType();
    const showImageControls = fileType === 'image' && !loading && !error;
    const showMediaControls = (fileType === 'video' || fileType === 'audio') && !loading && !error;

    return (
        <div className="file-viewer-container">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm flex-shrink-0">
                <div className="flex items-center space-x-4">
                    <h2 className="text-white font-semibold truncate max-w-md">
                        {file.original_name || file.name}
                    </h2>
                    <span className="text-gray-400 text-sm">
                        {file.formatted_size || ''}
                    </span>
                </div>

                <div className="flex items-center space-x-2">
                    {/* Navigation */}
                    {hasPrevious && (
                        <button
                            onClick={onPrevious}
                            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                            title="Previous file"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                    )}

                    {hasNext && (
                        <button
                            onClick={onNext}
                            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                            title="Next file"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    )}

                    <div className="h-6 w-px bg-gray-600 mx-2" />

                    {/* Image Controls */}
                    {showImageControls && (
                        <>
                            <button
                                onClick={handleZoomOut}
                                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                                title="Zoom out (-)"
                            >
                                <ZoomOut className="h-5 w-5" />
                            </button>

                            <span className="text-white text-sm min-w-[3rem] text-center">
                                {zoom}%
                            </span>

                            <button
                                onClick={handleZoomIn}
                                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                                title="Zoom in (+)"
                            >
                                <ZoomIn className="h-5 w-5" />
                            </button>

                            <button
                                onClick={handleZoomToFit}
                                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                                title="Zoom to fit (0)"
                            >
                                <Maximize2 className="h-5 w-5" />
                            </button>

                            <button
                                onClick={handleRotate}
                                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                                title="Rotate (R)"
                            >
                                <RotateCw className="h-5 w-5" />
                            </button>

                            <div className="h-6 w-px bg-gray-600 mx-2" />
                        </>
                    )}

                    {/* Fullscreen */}
                    <button
                        onClick={handleFullscreen}
                        className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Fullscreen"
                    >
                        <Maximize className="h-5 w-5" />
                    </button>

                    {/* Actions */}
                    <button
                        onClick={() => onStar(file)}
                        className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                        title={file.is_starred ? "Unstar" : "Star"}
                    >
                        <Star className={`h-5 w-5 ${file.is_starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                    </button>
                    {console.log(file)}
                    {(!file.is_shared  || (file.is_shared && file.uploaded_by == user.id)) &&
                        <button
                            onClick={() => onShare(file)}
                            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                            title="Share"
                        >
                            <Share2 className="h-5 w-5" />
                        </button>
                    }


                    <button
                        onClick={() => onDownload(file)}
                        className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Download"
                    >
                        <Download className="h-5 w-5" />
                    </button>

                    <button
                        onClick={onClose}
                        className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Content - CRITICAL FIX: Ensure proper flex layout for text files */}
            <div
                ref={viewerRef}
                className="flex-1 relative"
                style={{
                    height: 'calc(100% - 120px)', // Account for header and footer
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {renderFileContent()}
            </div>

            {/* Footer with keyboard shortcuts */}
            <div className="p-2 bg-black/50 backdrop-blur-sm flex-shrink-0">
                <div className="flex justify-center space-x-6 text-xs text-gray-400">
                    <span>ESC: Close</span>
                    {(hasNext || hasPrevious) && <span>← →: Navigate</span>}
                    {showImageControls && <span>+/-: Zoom</span>}
                    {showImageControls && <span>R: Rotate</span>}
                    {showImageControls && <span>0: Fit</span>}
                    {showImageControls && zoom > 100 && <span>Drag: Pan</span>}
                    {showImageControls && <span>Scroll: Zoom</span>}
                    {showMediaControls && <span>Space: Play/Pause</span>}
                </div>
            </div>
        </div>
    );
};

export default FileViewer;